import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import TextInputField from 'components/fields/text-input-field/text-input-field.component'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import { EditModeProvider } from 'contexts/edit-mode.context'
import { useEffect, useRef, useState } from 'react'
import useSubmitLock from 'hooks/use-submit-lock'
import { FormProvider } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import {
  useGetUserByIdQuery,
  usePatchUserByIdMutation,
} from 'store/auth/auth.api'
import { setUser } from 'store/auth/auth.slice'
import { useCreateIntegrationMutation, useGetIntegrationByIdQuery } from 'store/integrations/integrations.api'
import { getUserId } from 'utils/common'
import BorderConnect from '../../assets/images/borderconnect.png'
import { useEnableBorderConnectForm } from './enable-borderconnect-form.hooks'

type EnableBorderConnectDialogProps = {
  handleClose: () => void
  mode?: 'enable' | 'view'
  integrationId?: string | null
} & DialogProps

const FieldHelper = ({ children }: { children: string }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ mt: -0.5, display: 'block' }}
  >
    {children}
  </Typography>
)

const EnableBorderConnectDialog = ({
  open,
  handleClose,
  mode = 'enable',
  integrationId,
}: EnableBorderConnectDialogProps) => {
  const dispatch = useDispatch()
  const userId = getUserId()
  const formMethods = useEnableBorderConnectForm()
  const [errorMessage, setErrorMessage] = useState('')
  const isView = mode === 'view'
  const { isSubmitting, wrapFormSubmit } = useSubmitLock()

  const { data: user } = useGetUserByIdQuery({ userId }, { skip: !userId })
  const { data: integration } = useGetIntegrationByIdQuery(
    { integrationId: integrationId ?? '' },
    { skip: !open || !isView || !integrationId }
  )
  const [patchUser] = usePatchUserByIdMutation()
  const [createIntegration, { isLoading }] = useCreateIntegrationMutation()
  const isBusy = isLoading || isSubmitting

  const resetValuesRef = useRef({
    companyName: user?.companyName,
    scacCode: integration?.scacCode,
    carrierCode: integration?.carrierCode,
    companyKey: integration?.companyKey,
  })
  resetValuesRef.current = {
    companyName: user?.companyName,
    scacCode: integration?.scacCode,
    carrierCode: integration?.carrierCode,
    companyKey: integration?.companyKey,
  }

  useEffect(() => {
    if (!open) return
    const current = resetValuesRef.current
    formMethods.reset({
      legalCompanyName: current.companyName ?? '',
      scacCode: isView ? current.scacCode ?? '' : '',
      carrierCode: isView ? current.carrierCode ?? '' : '',
      companyKey: isView ? current.companyKey ?? '' : '',
    })
    setErrorMessage('')
  }, [open, isView, formMethods])

  useEffect(() => {
    if (!open || !user?.companyName) return
    if (!formMethods.getValues('legalCompanyName')) {
      formMethods.setValue('legalCompanyName', user.companyName)
    }
  }, [open, user?.companyName, formMethods])

  useEffect(() => {
    if (!open || !isView || !integration) return
    formMethods.setValue('scacCode', integration.scacCode ?? '')
    formMethods.setValue('carrierCode', integration.carrierCode ?? '')
    formMethods.setValue('companyKey', integration.companyKey ?? '')
  }, [open, isView, integration, formMethods])

  const handleEnable = async (values: {
    legalCompanyName: string
    scacCode: string
    carrierCode: string
    companyKey: string
  }) => {
    try {
      setErrorMessage('')
      const companyName = values.legalCompanyName.trim()
      const storedCompanyName = user?.companyName?.trim() ?? ''

      if (!storedCompanyName || storedCompanyName !== companyName) {
        const updatedUser = await patchUser({
          id: userId,
          companyName,
        }).unwrap()
        dispatch(setUser({ user: updatedUser }))
      }

      await createIntegration({
        userId,
        scacCode: values.scacCode,
        carrierCode: values.carrierCode,
        companyKey: values.companyKey.trim(),
        enabled: true,
        integrationType: 'BORDERCONNECT',
      }).unwrap()

      handleClose()
    } catch (err: any) {
      const message =
        err?.data?.message ||
        (err?.status && err?.error
          ? `${err.status}: ${err.error}`
          : 'Failed to enable BorderConnect')
      setErrorMessage(message)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ px: 3, pt: 2.5, pb: 1.5, bgcolor: 'transparent' }}
        >
          <img
            src={BorderConnect}
            alt="BorderConnect"
            width={140}
            height={32}
            style={{ objectFit: 'contain', objectPosition: 'left' }}
          />
          <Typography fontWeight={700} fontSize={18} sx={{ flex: 1 }}>
            {isView
              ? 'BorderConnect Integration'
              : 'Enable BorderConnect Integration'}
          </Typography>
          <IconButton
            onClick={handleClose}
            aria-label="Close"
            disableRipple
            sx={{
              color: 'error.main',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <FormProvider {...formMethods}>
          <form
            onSubmit={
              isView
                ? (e) => e.preventDefault()
                : wrapFormSubmit(formMethods.handleSubmit, handleEnable)
            }
          >
            <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
              <EditModeProvider key={mode} initialEditMode={!isView} suppressAutofill>
                <Stack spacing={2.5}>
                  <Stack spacing={0.5}>
                    <TextInputField
                      name="legalCompanyName"
                      label="Legal Company Name *"
                      placeholder="Enter legal company name"
                    />
                    <FieldHelper>
                      Name of your company as registered (must match BorderConnect
                      account).
                    </FieldHelper>
                  </Stack>
                  <Stack spacing={0.5}>
                    <TextInputField
                      name="scacCode"
                      label="SCAC Code (PAPS) *"
                      placeholder="Enter 4-character SCAC code"
                      maxLength={4}
                      transform={(value) => value.toUpperCase()}
                    />
                    <FieldHelper>
                      First 4 characters of your carrier&apos;s PAPS code (US).
                    </FieldHelper>
                  </Stack>
                  <Stack spacing={0.5}>
                    <TextInputField
                      name="carrierCode"
                      label="Canadian Carrier Code (PARS) *"
                      placeholder="Enter 4-character Canadian Carrier Code"
                      maxLength={4}
                      transform={(value) => value.toUpperCase()}
                    />
                    <FieldHelper>
                      First 4 characters of your carrier&apos;s PARS code (Canada).
                    </FieldHelper>
                  </Stack>
                  <Stack spacing={0.5}>
                    <TextInputField
                      name="companyKey"
                      label="Company Key *"
                      placeholder="Enter company key"
                    />
                    <FieldHelper>
                      Company key from your BorderConnect account.
                    </FieldHelper>
                  </Stack>
                </Stack>
              </EditModeProvider>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E0E0E0' }}>
              {isView ? (
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    minWidth: 100,
                    borderRadius: 1,
                  }}
                >
                  Close
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleClose}
                    disabled={isBusy}
                    variant="outlined"
                    sx={{
                      textTransform: 'none',
                      minWidth: 100,
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isBusy}
                    sx={{
                      textTransform: 'none',
                      minWidth: 100,
                      borderRadius: 1,
                      '&.Mui-disabled': {
                        bgcolor: 'action.disabledBackground',
                        color: 'action.disabled',
                      },
                    }}
                  >
                    Enable
                  </Button>
                </>
              )}
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorMessage !== ''}
        handleClose={() => setErrorMessage('')}
      />
    </>
  )
}

export default EnableBorderConnectDialog
